import React, { Suspense, useEffect, useMemo, useState } from 'react';

import { SidebarNavigation } from '@flipdish/ui-library/components/organisms/SidebarNavigation';
import { type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import FlipdishAppBanner from 'components/FlipdishAppBanner/FlipdishAppBanner';
import { connect } from 'react-redux';
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router';

import Loading from '@fd/ui/Loading';

import { appsActions } from '../../actions/apps.actions';
import FlipdishAgentAssistant from '../../components/FlipdishAgent/FlipdishAgentAssistant';
import { getOrgByBrandId } from '../../components/RMS/organisation.services';
import { setCurrentOrg } from '../../components/RMS/rms.actions';
import { generalConstants } from '../../constants/general.constants';
import { useIsWebView } from '../../custom-hooks/useIsWebView';
import useTailwindPortal from '../../custom-hooks/useTailwindPortal';
import { lazyWithRetry } from '../../helpers/utilities';
import { portalRoutes } from '../../routes/portal.routes';
import { flagService } from '../../services';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useBooleanFeatureFlagResolution } from '../../services/featureFlags/useBooleanFeatureFlagResolution';
import { useFeatureFlagValue } from '../../services/featureFlags/useFeatureFlagValue';
import { type PortalUser, shouldDisplay } from '../../viewConfiguration';
import NewSidebarPortalStyles from './NewSidebarPortalStyles';
import PortalShell from './PortalShell/PortalShell';
import ScrollToTop from './ScrollToTop';
import { useSidebarNavItems } from './sidebar/hooks/useSidebarNavItems';
import TopNav from './TopNav';

const isDev = !window.location?.host?.includes('portal.flipdish.com');
const FlipdishToolbar = lazyWithRetry(
  () => import('../../components/FlipdishToolbar/FlipdishToolbar')
);

const { SCROLLABLE_CONTAINER } = generalConstants;

const tutorialDrawerWidth = 408;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  // 200: above the app's ad-hoc z-indexes (max 101, COG-759), below MUI modal (1300). Remove with MUI.
  sidebarNavLayer: {
    flexShrink: 0,
    position: 'relative',
    zIndex: 200,
    '@media (min-width: 1024px)': {
      '& > .invisible': { display: 'block !important' },
    },
  },
  rightSide: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },
  content: {
    flexGrow: 1,
    width: '100%',
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    overflowX: 'auto',
  },
  switch: {
    flexGrow: 1,
    display: 'flex',
    overflowY: 'auto',
    overflowX: 'hidden',
    flexDirection: 'column',
  },
  tutorialDrawerOpen: {
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.up('sm')]: {
      marginRight: tutorialDrawerWidth,
    },
  },
  tutorialDrawerClose: {
    marginRight: 0,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
}));

const createRoute = (
  props: PortalRoutePropsExtended | PortalRedirectPropsExtended,
  portalUser: PortalUser
) => {
  if ('to' in props) {
    // only Redirect has `to` prop required
    const { name, key = '', ...rest } = props;
    return <Redirect {...rest} key={`${name}.${key}`} />;
  }

  const { permissions, viewId, exemptFromViewConfiguration, name, key = '', ...rest } = props;
  if (
    shouldDisplay(portalUser, viewId, {
      legacyVisibilityPermissions: permissions,
      exemptFromViewConfiguration,
    })
  ) {
    return <Route {...rest} key={`${name}.${key}`} />;
  }
  return null;
};

type Props = MappedDispatch & MappedState;

const Portal = (props: Props) => {
  const {
    currentApp,
    currentOrg,
    disablePropertyLevelAddOns,
    disableLegacyTeammate,
    foodSafetyAddOnPermissions,
    getApps,
    isHelpDrawerOpen,
    permissions,
    account,
    dispatchSetCurrentOrg,
  } = props;
  const showNewApplicationHeader = useFeatureFlagValue(
    PortalFeatureFlags.showNewApplicationHeader,
    false
  );

  const history = useHistory();
  const location = useLocation();
  const { appId } = useParams<{ appId: string }>();
  const isWebView = useIsWebView();
  const isOnSetupRoute = Boolean(matchPath(location.pathname, { path: '/:appId/setup' }));

  const { data: orgData } = useQuery({
    queryKey: [getOrgByBrandId, appId],
    queryFn: () => getOrgByBrandId(appId || ''),
    enabled: !!appId,
    retry: 1,
  });

  useEffect(() => {
    const org = orgData?.data?.data?.[0];
    if (org && org.orgId !== currentOrg?.orgId) {
      dispatchSetCurrentOrg(org);
    }
  }, [orgData, currentOrg]);

  useEffect(() => {
    if (!isWebView) return;
    return history.listen((location, action) => {
      if (action === 'REPLACE') return;
      const params = new URLSearchParams(location.search);
      const alreadySet = [...params.entries()].some(
        ([k, v]) => k.toLowerCase() === 'iswebview' && v.toLowerCase() === 'true'
      );
      if (!alreadySet) {
        params.set('isWebView', 'true');
        history.replace({ ...location, search: `?${params.toString()}` });
      }
    });
  }, [isWebView]);

  // Redirect to onboarding if conditions are met
  //useRedirectToOnboarding();

  const classes = useStyles();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sidebarNav = useSidebarNavItems();
  const {
    items: navItems,
    footerItems: navFooterItems,
    account: navAccount,
    activeUrl,
    onNavigate: onSidebarNavigate,
  } = sidebarNav;
  useTailwindPortal();

  useEffect(() => {
    getApps();
  }, []);

  const drawerOpen = isHelpDrawerOpen === true;
  const drawerClosed = isHelpDrawerOpen === false;

  const useAuthZForGetApps = useBooleanFeatureFlagResolution(
    PortalFeatureFlags.backend_auth_useAuthZForGetApps,
    false
  );
  const useAuthZPermissions = useAuthZForGetApps.status === 'resolved' && useAuthZForGetApps.value;

  const routes = useMemo(() => {
    // Brand switches call clearPermissions() before loading the new brand's permissions
    // (selectedAppChanged in apps.actions.ts). Rendering routes during that empty window
    // causes every permission-gated route to drop out and lets the '*' wildcard redirect
    // send the user to /home — so wait for the replacement permissions before rendering.
    if (!currentApp.AppId || !permissions.length) {
      return null;
    }
    const portalUser = {
      userId: account?.AccountId,
      role: currentApp.AppAccessLevel,
      permissions,
    };
    return portalRoutes(
      currentApp.AppId,
      disablePropertyLevelAddOns,
      foodSafetyAddOnPermissions,
      useAuthZPermissions
    )
      .filter((r) => !(disableLegacyTeammate && 'name' in r && r.name === 'teammates'))
      .map((r) => createRoute(r, portalUser));
  }, [
    currentApp.AppId,
    currentApp.AppAccessLevel,
    permissions,
    account?.AccountId,
    disableLegacyTeammate,
    disablePropertyLevelAddOns,
    foodSafetyAddOnPermissions,
    useAuthZPermissions,
  ]);

  const pageContent = routes ? (
    <Switch>{routes}</Switch>
  ) : (
    <Loading data-fd="circular-progress" style={{ flexGrow: 1, minHeight: 240 }} />
  );

  const devTools = isDev ? (
    <Suspense fallback={null}>
      <FlipdishToolbar />
    </Suspense>
  ) : null;

  // isWebView keeps the sidebar-only layout below, which already hides every piece of chrome.
  if (showNewApplicationHeader && !isWebView) {
    return (
      <PortalShell sidebar={sidebarNav} isHelpDrawerOpen={isHelpDrawerOpen} extras={devTools}>
        {!isOnSetupRoute && <FlipdishAppBanner />}
        <ScrollToTop>{pageContent}</ScrollToTop>
      </PortalShell>
    );
  }

  return (
    <div className={classes.root}>
      <NewSidebarPortalStyles />
      {!isWebView && (
        <div data-tailwind className={classes.sidebarNavLayer}>
          <SidebarNavigation
            activeUrl={activeUrl}
            items={navItems}
            footerItems={navFooterItems}
            account={navAccount}
            onNavigate={onSidebarNavigate}
            showMobileHeader={false}
            mobileMenuOpen={mobileMenuOpen}
            onMobileMenuOpenChange={setMobileMenuOpen}
          />
        </div>
      )}
      <div className={classes.rightSide}>
        {!isWebView && (
          <TopNav mobileMenuOpen={mobileMenuOpen} onMobileMenuOpenChange={setMobileMenuOpen} />
        )}
        <main className={classes.content} data-testid="portal-main-content">
          <div
            className={clsx({
              [classes.switch]: true,
              [classes.tutorialDrawerOpen]: drawerOpen,
              [classes.tutorialDrawerClose]: drawerClosed,
            })}
            id={SCROLLABLE_CONTAINER}
          >
            {!isOnSetupRoute && <FlipdishAppBanner />}
            <ScrollToTop>{pageContent}</ScrollToTop>
          </div>
        </main>
      </div>
      {devTools}
      {!isWebView && <FlipdishAgentAssistant />}
    </div>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
function mapStateToProps(state: AppState) {
  return {
    currentApp: state.currentApp,
    currentOrg: state.rms.currentOrg,
    account: state.account,
    isHelpDrawerOpen: state.drawer.helpDrawerOpen,
    permissions: state.permissions,
    disablePropertyLevelAddOns: flagService.isFlagOn(state, 'disablePropertyManagerLevelAddOns'),
    disableLegacyTeammate: flagService.isFlagOn(state, 'disableLegacyTeammate'),
    foodSafetyAddOnPermissions: flagService.isFlagOn(state, 'foodSafetyAddOnPermissions'),
  };
}

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
function mapDispatchToProps(dispatch: ThunkDispatch) {
  return {
    getApps: () => dispatch(appsActions.getApps(undefined, false, undefined, 1, 20)),
    dispatchSetCurrentOrg: (org: Parameters<typeof setCurrentOrg>[0]) =>
      dispatch(setCurrentOrg(org)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Portal);
